import type { AnchorAriaRoles, HTMLAttributes } from 'preact';
import { Link as RouterLink, useRouter } from '@tanstack/react-router';
import type { RegisteredRouter } from '@tanstack/react-router';
import { webappUrl } from '../config.ts';

// The marketing site (www) and the product app (app.movegoalpost.com) are
// separate origins after the webapp split. Internal marketing routes
// navigate via the SPA router; links to product/auth routes (signup, login,
// dashboard) must be real cross-origin anchors into the app. This wrapper
// picks the right behavior from the `to` path, so pages keep using a single
// `<Link to="…">` regardless of which side a target lives on.

// Preact types an anchor's `role` by whether it has an `href`. Every Link
// renders with one (derived from `to`), so accept the roles of that arm.
type HrefAnchorRole = Omit<Extract<AnchorAriaRoles, { href: unknown }>, 'href'>;

type LinkProps = Omit<HTMLAttributes<HTMLAnchorElement>, 'role'> & HrefAnchorRole & { to: string };

type MarketingRoute = keyof RegisteredRouter['routesByPath'];

// A path is a marketing route iff it's registered in this SPA's route tree
// (router.tsx) — consulted live from the router's path table, so newly
// registered pages are SPA links with no list to update. Product/auth paths
// aren't in the tree and fall through to a cross-origin anchor.
function isMarketingRoute(router: RegisteredRouter, to: string): to is MarketingRoute {
  return to in router.routesByPath;
}

export function Link({ to, ...rest }: LinkProps) {
  const router = useRouter();
  if (isMarketingRoute(router, to)) {
    // `to` is now a registered route literal, so RouterLink accepts it directly.
    return <RouterLink to={to} {...rest} />;
  }
  return <a href={webappUrl(to)} {...rest} />;
}
